/**
 * Parties — org-wide directory page.
 *
 * 1:1 with the folder's `parties.html`:
 *   .projects-header (title + stats + New Party button)
 *   .filter-bar (chips: All / Clients / Staff / Vendors / Labour Contractors + search)
 *   .proj-table-wrap > .proj-table (7-col table)
 *
 * Real data via `useParties()` (org-wide live subscription).
 * Row click opens the right-side preview (`PartySlideOver`).
 * "+ New Party" opens the form (`PartyFormSlideOver`).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PartyFormSlideOver } from '@/components/PartyFormSlideOver';
import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import {
  formatIndianPhone,
  partyTypeColors,
  partyTypeGroup,
  partyTypeLabel,
  partyTypePillColors,
  type Party,
  type PartyType,
} from '@/lib/parties';
import { useParties } from '@/lib/useParties';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { canPartyWrite } from '@shared/perms';

// ───── Filter model ─────

type ChipKey = 'all' | 'client' | 'staff' | 'vendor' | 'labour_contractor';

const CHIPS: { key: ChipKey; label: string; match: (t: PartyType) => boolean }[] = [
  { key: 'all',     label: 'All',                match: () => true },
  { key: 'client',  label: 'Clients',            match: (t) => t === 'client' },
  { key: 'staff',   label: 'Staff',              match: (t) => t === 'staff' || t === 'worker' },
  {
    key: 'vendor',
    label: 'Vendors',
    match: (t) =>
      t === 'material_supplier' ||
      t === 'equipment_supplier' ||
      t === 'other_vendor' ||
      t === 'contractor',
  },
  {
    key: 'labour_contractor',
    label: 'Labour Contractors',
    match: (t) => t === 'labour_contractor',
  },
];

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0] ?? '').join('').toUpperCase() || '?';
}

function relTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return new Date(ms).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).replace(' AM', ' AM').replace(' PM', ' PM');
  }
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

// ───── Page ─────

export function PartiesPage() {
  const { parties, loading, error, orgId } = useParties();
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canPartyWrite(orgRole);
  const [chip, setChip] = useState<ChipKey>('all');
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const [localSearch, setLocalSearch] = useState('');
  const navigate = useNavigate();

  // Form slide-over state (preview is now a full page at
  // `/parties/:partyId`, no URL-driven preview here).
  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; party: Party }
  >({ kind: 'closed' });

  useEffect(() => {
    setPlaceholder('Search parties by name, phone, email…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const stats = useMemo(() => {
    const month = new Date();
    month.setDate(1);
    month.setHours(0, 0, 0, 0);
    const cutoff = month.getTime();
    let newThisMonth = 0;
    let generalCount = 0;
    let vendorCount = 0;
    for (const p of parties) {
      const ms = p.createdAt?.toMillis?.() ?? 0;
      if (ms >= cutoff) newThisMonth += 1;
      const grp = partyTypeGroup(p.partyType);
      if (grp === 'General') generalCount += 1;
      else vendorCount += 1;
    }
    return {
      total: parties.length,
      newThisMonth,
      general: generalCount,
      vendor: vendorCount,
    };
  }, [parties]);

  const filtered = useMemo(() => {
    const def = CHIPS.find((c) => c.key === chip)!;
    const q = (searchQuery || localSearch).trim().toLowerCase();
    return parties.filter((p) => {
      if (!def.match(p.partyType)) return false;
      if (q) {
        const blob = [
          p.name,
          p.phone,
          p.email ?? '',
          p.address ?? '',
          partyTypeLabel(p.partyType),
        ].join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [parties, chip, searchQuery, localSearch]);

  return (
    <div className="page active" id="page-parties" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Parties</h1>
          <div className="projects-stats">
            <span>{stats.total}</span> total parties ·{' '}
            <span>{stats.newThisMonth}</span> new this month
          </div>
        </div>
        {canWrite && (
          <button
            className="btn-primary"
            type="button"
            onClick={() => setFormMode({ kind: 'create' })}
            disabled={!orgId}
          >
            <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> New Party
          </button>
        )}
      </div>

      {/* Summary tile — three-cell breakdown of the contact book.
          Same shape as the Team page summary card so the studio's
          counts feel consistent across the two directories. */}
      <div
        className="card"
        style={{
          padding: 0,
          overflow: 'hidden',
          marginBottom: 16,
          maxWidth: 480,
        }}
      >
        <div style={{ display: 'flex', padding: 14 }}>
          <SummaryCell label="TOTAL" value={stats.total} />
          <SummaryDivider />
          <SummaryCell label="GENERAL" value={stats.general} />
          <SummaryDivider />
          <SummaryCell label="VENDOR" value={stats.vendor} />
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar">
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${chip === c.key ? ' active' : ''}`}
            onClick={() => setChip(c.key)}
          >
            {c.label}
          </button>
        ))}
        <div className="filter-sep" />
        <div className="filter-search-wrap" style={{ flex: 1 }}>
          <i className="ph ph-magnifying-glass" />
          <input
            className="filter-search"
            type="search"
            placeholder="Search parties by name or phone…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading parties…
        </div>
      ) : error ? (
        <div
          className="card"
          style={{ padding: 32, textAlign: 'center', color: 'var(--critical)' }}
        >
          Couldn't load: {error}
        </div>
      ) : parties.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>
            <i className="ph ph-address-book" />
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
            No parties yet
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginBottom: 16,
            }}
          >
            Clients, staff, workers, and vendors — they all live here. Add your first to start logging transactions.
          </div>
          {canWrite && (
            <button
              className="btn-primary"
              type="button"
              onClick={() => setFormMode({ kind: 'create' })}
            >
              <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> Add your first party
            </button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 14 }}>No parties match this filter.</div>
        </div>
      ) : (
        <div className="proj-table-wrap">
          <table className="proj-table">
            <thead>
              <tr>
                <th style={{ width: 300 }}>Party Name</th>
                <th>Type</th>
                <th>Contact</th>
                <th className="right">Advance Paid</th>
                <th className="right">Payable Balance</th>
                <th>Last Active</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const colors = partyTypeColors(p.partyType);
                const pill = partyTypePillColors(p.partyType);
                const opening = p.openingBalance ?? 0;
                const isToReceive = p.openingBalanceType === 'to_receive';
                const advance = opening > 0 && isToReceive ? opening : 0;
                const payable = opening > 0 && !isToReceive ? opening : 0;
                return (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/parties/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="proj-name-cell">
                        <div
                          className="proj-thumb"
                          style={{ background: colors.bg, color: colors.fg }}
                        >
                          {initials(p.name)}
                        </div>
                        <div className="proj-name-text">
                          <div className="proj-name-title">{p.name}</div>
                          <div className="proj-name-client">
                            {p.address?.trim() || '—'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className="stage-pill"
                        style={{ background: pill.bg, color: pill.fg }}
                      >
                        {partyTypeLabel(p.partyType)}
                      </span>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {formatIndianPhone(p.phone)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {p.email ?? '—'}
                      </div>
                    </td>
                    <td
                      className="right tn"
                      style={{
                        color: advance > 0 ? 'var(--success)' : 'var(--text-secondary)',
                        fontWeight: advance > 0 ? 600 : 400,
                      }}
                    >
                      {advance > 0 ? `₹${advance.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td
                      className="right tn"
                      style={{
                        color: payable > 0 ? 'var(--critical)' : 'var(--text-secondary)',
                        fontWeight: payable > 0 ? 600 : 400,
                      }}
                    >
                      {payable > 0 ? `₹${payable.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {relTime(p.createdAt?.toMillis() ?? null)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-icon"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/parties/${p.id}`);
                        }}
                        aria-label="Open party"
                      >
                        <i className="ph ph-dots-three" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Form slide-over (create + edit) */}
      <PartyFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        party={formMode.kind === 'edit' ? formMode.party : null}
        onSaved={(id) => {
          setFormMode({ kind: 'closed' });
          navigate(`/parties/${id}`);
        }}
      />
    </div>
  );
}

// ───── Summary-tile helpers ─────

function SummaryCell({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: 'var(--text-primary)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          letterSpacing: 0.5,
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function SummaryDivider() {
  return (
    <div
      style={{
        width: 1,
        background: 'var(--border)',
        marginLeft: 8,
        marginRight: 8,
      }}
    />
  );
}
